import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'router';
import { ClickTracker } from 'components/Tracking';
import { Text } from 'components';
import { useI18n } from 'components/I18n';
import { MENU_ITEM_TYPES, MENU_LINK_TARGET_TYPES } from 'utils/constants';
import * as pageActions from 'actions/page';
import { resetScrollableOffset } from 'actions/scrollable';
import { MenuContainer, MenuLink, Nav, SignUpButton } from './Styles';


function TopNavigation(props) {
  const {
    position,
    innerRef,
    shouldShowSignUp,
    visibleMenuItems,
    defaultMenuItem,
    dropdown = false,
    onItemClick,
  } = props;

  const {
    location,
    page,
    collectionRefreshThreshold,
  } = useSelector(state => ({
    location: state.router.location,
    page: state.page,
    collectionRefreshThreshold: state.settings.features.collectionRefreshThresholdInMin
      ? state.settings.features.collectionRefreshThresholdInMin * 60 * 1000
      : undefined,
  }), shallowEqual);

  const dispatch = useDispatch();
  const router = useRouter();
  const i18n = useI18n();

  useEffect(() => {
    const identifier = location.name === 'home'
      ? defaultMenuItem?.identifier
      : location.params.id;
    if (identifier && !page[identifier]) {
      dispatch(pageActions.UpdatePage({
        id: identifier,
        lastViewDate: new Date(),
        isExpired: false,
      }));
    }
  }, [location.name, defaultMenuItem, page, dispatch]);

  const handleContentListMenuClick = (item) => {
    const now = new Date();
    const isExpired = page[item.identifier] && !page[item.identifier].isExpired
    && collectionRefreshThreshold
      ? (now - page[item.identifier].lastViewDate) > collectionRefreshThreshold
      : false;
    if (isExpired) {
      dispatch(pageActions.UpdatePage({
        id: item.identifier,
        lastViewDate: new Date(),
        isExpired: true,
      }));
    }

    dispatch(resetScrollableOffset(item.identifier));
  };

  const renderMenuItems = () => {
    const currentUrl = router.getUrl(location, false);

    return visibleMenuItems.map((item) => {
      let menuLink;

      if (defaultMenuItem === item) { // home
        menuLink = (
          <MenuLink
            key="home"
            to={{ name: 'home' }}
            data-content={item.title}
            active={location.name === 'home'}
            position={position}
            role={dropdown ? 'menuitem' : undefined}
            onClick={() => {
              if (item.type === MENU_ITEM_TYPES.contentList) {
                handleContentListMenuClick(item);
              }
              onItemClick?.();
            }}
          >
            {item.title}
          </MenuLink>
        );
      } else if (item.type === MENU_ITEM_TYPES.link) {
        if (item.linkTarget === MENU_LINK_TARGET_TYPES.webview) {
          menuLink = (
            <MenuLink
              key={`webview-${item._id}`}
              to={{ name: 'webview', params: { id: item._id } }}
              data-content={item.title}
              active={location.name === 'webview' && location.params.id === item._id}
              position={position}
              role={dropdown ? 'menuitem' : undefined}
              onClick={onItemClick}
            >
              {item.title}
            </MenuLink>
          );
        } else {
          const target = item.linkTarget === MENU_LINK_TARGET_TYPES.blank ? '_blank' : undefined;
          menuLink = (
            <MenuLink
              key={item.url}
              href={item.url}
              target={target}
              data-content={item.title}
              active={currentUrl === item.url}
              position={position}
              role={dropdown ? 'menuitem' : undefined}
              onClick={onItemClick}
            >
              {item.title}
            </MenuLink>
          );
        }
      } else if (item.type === MENU_ITEM_TYPES.epg) {
        menuLink = (
          <MenuLink
            key="epg"
            to={{ name: 'epg' }}
            data-content={item.title}
            active={location.name === 'epg'}
            position={position}
            role={dropdown ? 'menuitem' : undefined}
            onClick={onItemClick}
          >
            {item.title}
          </MenuLink>
        );
      } else if (item.type === MENU_ITEM_TYPES.contentList) {
        menuLink = (
          <MenuLink
            key={`collections-${item.identifier}`}
            to={{ name: 'collections', params: { id: item.identifier } }}
            data-content={item.title}
            active={location.name === 'collections' && location.params.id === item.identifier}
            position={position}
            role={dropdown ? 'menuitem' : undefined}
            onClick={() => {
              handleContentListMenuClick(item);
              onItemClick?.();
            }}
          >
            {item.title}
          </MenuLink>
        );
      }

      if (!menuLink) {
        return null;
      }

      return (
        <ClickTracker
          key={item.title}
          component="Menu"
          clickType="navigation"
          eventName={item.trackingEvent}
          collectionId={item.identifier}
        >
          {menuLink}
        </ClickTracker>
      );
    }).filter(Boolean);
  };

  const menuContent = (
    <MenuContainer position={position}>
      {renderMenuItems()}
      {shouldShowSignUp && (
        <ClickTracker
          component="Menu"
          clickType="navigation"
          eventName="SignUp"
        >
          <SignUpButton
            to={{ name: 'create-account' }}
            data-content={i18n.formatText('menu.signUp')}
            active={location.name === 'create-account'}
            position={position}
            role={dropdown ? 'menuitem' : undefined}
            onClick={onItemClick}
          >
            <Text id="menu.signUp" />
          </SignUpButton>
        </ClickTracker>
      )}
    </MenuContainer>
  );

  if (dropdown) {
    return (
      <>
        {menuContent}
      </>
    );
  }

  return (
    <Nav
      position={position}
      data-cy={`top-navigation-${position}`}
      aria-label={i18n.formatText('aria.navigation.mainLabel')}
      {...(innerRef ? { innerRef } : {})}
    >
      {menuContent}
    </Nav>
  );
}

TopNavigation.propTypes = {
  position: PropTypes.oneOf(['vertical', 'horizontal']).isRequired,
  innerRef: PropTypes.object,
  shouldShowSignUp: PropTypes.bool,
  visibleMenuItems: PropTypes.array.isRequired,
  defaultMenuItem: PropTypes.object,
  dropdown: PropTypes.bool,
  onItemClick: PropTypes.func,
};

export default React.memo(TopNavigation);
